import { useState } from "react";
import { FiX, FiTerminal, FiAward, FiUser, FiChevronDown, FiFileText, FiExternalLink } from "react-icons/fi";

function MyProfileDetails({ setShowOverlay, profile, profileImage }) {
  const [isCertDrawerOpen, setIsCertDrawerOpen] = useState(false);
  const [isEduDrawerOpen, setIsEduDrawerOpen] = useState(false);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xl sm:p-8"
      onClick={() => setShowOverlay(false)}
    >
      <div
        className="relative flex w-full max-w-lg max-h-[90vh] flex-col animate-fade-up overflow-hidden rounded-3xl border border-white/10 bg-[#0a0f1c]/90 shadow-2xl shadow-indigo-500/10 backdrop-blur-2xl transition-all duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute -left-20 -top-20 z-0 h-40 w-40 rounded-full bg-indigo-500/20 blur-3xl"></div>
        <div className="pointer-events-none absolute -bottom-20 -right-20 z-0 h-40 w-40 rounded-full bg-purple-500/20 blur-3xl"></div>

        <button
          onClick={() => setShowOverlay(false)}
          className="absolute right-4 top-4 z-30 rounded-full bg-white/5 p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white sm:right-6 sm:top-6"
        >
          <FiX className="h-5 w-5" />
        </button>

        <div className="relative z-10 flex w-full flex-col items-center overflow-y-auto p-5 pt-14 text-center sm:p-8 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="mb-6 flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-indigo-500/30 bg-indigo-500/10 shadow-[0_0_20px_rgba(99,102,241,0.2)] sm:h-24 sm:w-24">
            {profileImage ? (
              <img
                src={profileImage}
                alt={profile.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <FiUser className="h-10 w-10 text-indigo-400" />
            )}
          </div>

          <h2 className="mb-2 bg-gradient-to-r from-white via-indigo-100 to-indigo-300 bg-clip-text text-2xl font-black tracking-tight text-transparent sm:text-3xl md:text-4xl">
            {profile.name}
          </h2>

          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-1.5 text-xs font-semibold text-indigo-300 sm:text-sm">
            <FiTerminal className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{profile.role}</span>
          </div>

          <p className="mb-8 text-xs italic text-slate-500 sm:text-sm">
            {profile.experience} years of experience
          </p>

          {/* Information Cards Container */}
          <div className="w-full flex flex-col gap-3 pb-4">
            {/* Education Drawer */}
            {profile.educations?.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] transition-colors hover:bg-white/[0.04]">
                <button
                  onClick={() => setIsEduDrawerOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between p-4 text-left focus:outline-none group sm:p-5"
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-400 transition-transform group-hover:scale-110 group-hover:bg-purple-500/20 sm:h-12 sm:w-12">
                      <FiAward className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                        Academic Background
                      </p>
                      <p className="mt-0.5 text-sm font-medium leading-relaxed text-slate-200 sm:mt-1 sm:text-base">
                        View Education
                      </p>
                    </div>
                  </div>
                  <div className={`rounded-full bg-white/5 p-2 transition-transform duration-300 ${isEduDrawerOpen ? "rotate-180" : "rotate-0"}`}>
                    <FiChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </button>

                <div className={`grid transition-all duration-300 ease-in-out ${isEduDrawerOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="flex flex-col gap-3 border-t border-white/5 p-4 pt-3 text-left sm:gap-4 sm:p-5 sm:pt-4">
                      {profile.educations.map((edu) => (
                        <div key={edu.id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white/[0.02] p-3 sm:p-4">
                          <div className="flex flex-col justify-center">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                              Degree
                            </p>
                            <p className="mt-0.5 text-sm font-medium leading-relaxed text-slate-200 sm:mt-1 sm:text-base">
                              {edu.title}
                            </p>
                          </div>
                          <div className="flex flex-col justify-center sm:text-right">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                              Years
                            </p>
                            <p className="mt-0.5 text-sm font-medium leading-relaxed text-slate-200 sm:mt-1 sm:text-base">
                              {edu.time}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {profile.certificates?.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] transition-colors hover:bg-white/[0.04]">
                <button
                  onClick={() => setIsCertDrawerOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between p-4 text-left focus:outline-none group sm:p-5"
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 transition-transform group-hover:scale-110 group-hover:bg-blue-500/20 sm:h-12 sm:w-12">
                      <FiFileText className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                        Achievements
                      </p>
                      <p className="mt-0.5 text-sm font-medium leading-relaxed text-slate-200 sm:mt-1 sm:text-base">
                        View Certificates
                      </p>
                    </div>
                  </div>
                  <div className={`rounded-full bg-white/5 p-2 transition-transform duration-300 ${isCertDrawerOpen ? "rotate-180" : "rotate-0"}`}>
                    <FiChevronDown className="h-4 w-4 text-slate-400" />
                  </div>
                </button>

                <div className={`grid transition-all duration-300 ease-in-out ${isCertDrawerOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="flex flex-col gap-2 border-t border-white/5 p-4 pt-3 text-left sm:gap-3 sm:p-5 sm:pt-4">
                      {profile.certificates.map((cert) => (
                        <a
                          key={cert.id}
                          href={cert.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/link flex items-center justify-between rounded-xl bg-white/[0.03] p-3 transition-colors hover:bg-white/[0.08] sm:p-4"
                        >
                          <span className="text-xs font-medium text-slate-300 transition-colors group-hover/link:text-white sm:text-sm">
                            {cert.name}
                          </span>
                          <FiExternalLink className="h-4 w-4 shrink-0 text-slate-500 transition-colors group-hover/link:text-indigo-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyProfileDetails;